import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, ShieldCheck } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout';
import { useAnimatedNumber } from '@/hooks/useAnimatedNumber';
import { HoverBorderGradient } from '../ui/hover-border-gradient';
import GlassToast, { type ToastType } from '../ui/GlassToast';
import SectionDivider from './SectionDivider';

/**
 * Pricing — modeled on the "Pricing 1" block layout from React Bits Pro
 * (a free-trial banner up top, then three cards with the middle one lit up
 * by a green radial glow), built by hand rather than installed — that block
 * is a paid registry component requiring a REACTBITS_LICENSE_KEY this
 * project doesn't have. Same visual shape, our own real data and code.
 *
 * The reference layout has 4 slots (banner + 3 cards); our backend has 4
 * conceptual plan groups (free, contracts, unlimited, evidence), so the
 * mapping is direct: Free → the banner, Contracts/Unlimited/Evidence → the
 * three cards, Unlimited (already flagged highlight:true by the backend)
 * takes the lit-up middle slot. Still pulled live from GET /billing/plans.
 */

type Plan = {
  id: number;
  code: string;
  name: string;
  description: string;
  price_paise: number;
  currency: string;
  period: 'one_time' | 'monthly';
  contract_credits: number;
  cert_credits: number;
  features: string[] | null;
  highlight: boolean;
};

function rupees(paise: number): string {
  const v = paise / 100;
  return `₹${v % 1 === 0 ? v.toLocaleString('en-IN') : v.toFixed(2)}`;
}

const CONTRACT_TIER_CODES = ['single', 'pack3', 'pack10'];

function WhiteCta({ label, busy, onClick }: { label: string; busy: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="w-full rounded-full bg-white text-black text-sm font-bold px-7 py-3 flex items-center justify-center gap-2 hover:bg-white/90 transition-colors cursor-pointer disabled:opacity-60"
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : label}
    </button>
  );
}

function FeatureList({ features }: { features: string[] }) {
  return (
    <ul className="flex flex-col gap-2.5 mb-6 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/75">
          <Check size={14} className="text-[#3cb44f] shrink-0 mt-0.5" />
          {f}
        </li>
      ))}
    </ul>
  );
}

export default function PricingRevamp() {
  const navigate = useNavigate();
  const { checkout, busyPlan } = useRazorpayCheckout();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierIndex, setTierIndex] = useState(0); // default to the 1-contract tier (single)
  const [toast, setToast] = useState<{ message: string; type: ToastType; title?: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/billing/plans');
        const data = (res as any).data?.data ?? (res as any).data;
        if (Array.isArray(data) && data.length > 0) setPlans(data);
      } catch {
        // Section just doesn't render below — no broken UI to show for it.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isAuthed = (() => {
    try {
      return Boolean(window.localStorage.getItem('access_token'));
    } catch {
      return false;
    }
  })();

  const handleChoose = async (plan: Plan) => {
    const isFree = plan.price_paise === 0;
    if (isFree || !isAuthed) {
      navigate('/signup');
      return;
    }
    // No coupon field here on purpose — coupons apply after account
    // creation, at actual purchase time, not on the public pricing section.
    const result = await checkout(plan.code);
    if (result.ok) {
      setToast({ type: 'success', title: 'Payment successful', message: `${plan.name} is now active on your account.` });
      window.setTimeout(() => navigate('/dashboard/contracts'), 1600);
    } else if (result.reason !== 'cancelled') {
      setToast({ type: 'error', title: 'Payment not completed', message: result.message });
    }
  };

  const contractTiers = CONTRACT_TIER_CODES.map((code) => plans.find((p) => p.code === code)).filter(
    (p): p is Plan => Boolean(p)
  );
  const selectedTier = contractTiers[Math.min(tierIndex, contractTiers.length - 1)];

  // Hooks can't sit behind the early return below, so this is computed here
  // even though selectedTier might not exist yet — falls back to 0 rather
  // than skipping the hook call.
  const animatedTierRupees = useAnimatedNumber(selectedTier ? selectedTier.price_paise / 100 : 0, 450);

  if (!loading && plans.length === 0) return null;

  const freePlan = plans.find((p) => p.code === 'free');
  const unlimitedPlan = plans.find((p) => p.code === 'unlimited');
  const evidencePlan = plans.find((p) => p.code === 'cert65b');

  return (
    <>
      <SectionDivider />
      <section id="pricing" className="relative bg-black py-20 md:py-24 scroll-mt-24">
        <div className="px-6 max-w-5xl mx-auto">
          <div className="max-w-2xl mx-auto text-center mb-10 md:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
            >
              {/* <span className="inline-flex items-center gap-2 rounded-full border border-[#3cb44f]/25 px-3.5 py-1.5 mb-6 font-mono text-[10px] tracking-[0.25em] text-[#3cb44f]/90 uppercase">
              <span className="w-1 h-1 rounded-full bg-[#3cb44f]" />
              Pricing
            </span> */}
              <h2 className="font-bricolage font-extrabold text-3xl md:text-5xl text-white leading-[1.15] tracking-tight text-balance mb-4">
                Start free. Pay only when you{' '}
                <span className="font-instrumentserif italic font-normal text-accent">need</span> more.
              </h2>
              <p className="text-white/55 text-sm md:text-base leading-relaxed">
                Your first contract is free. After that, buy exactly what you need — no subscription forced, and no
                cut taken from what you earn, ever.
              </p>
            </motion.div>
          </div>

          {loading ? (
            <div className="flex flex-col gap-5 animate-pulse">
              <div className="rounded-full border border-white/10 bg-white/[0.02] h-20" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-3xl border border-white/10 bg-white/[0.02] h-[420px]" />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* ── Free — the pill banner up top, shimmer border ───────── */}
              {freePlan && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.45 }}
                  className="mb-6"
                >
                  {/* HoverBorderGradient's moving-gradient border rotates
                    continuously even at rest (the hover state only adds an
                    extra highlight burst on top) — reused here purely for
                    that always-on shimmer, not for its hover behavior. */}
                  <HoverBorderGradient
                    as="div"
                    duration={2.2}
                    staticBeam
                    containerClassName="rounded-[28px] w-full"
                    className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-black px-7 py-5"
                  >
                    <div>
                      <p className="font-bricolage text-lg font-bold text-white">Start free</p>
                      <p className="flex items-center gap-1.5 text-[13px] text-white/45 mt-0.5">
                        {freePlan.contract_credits} contract included
                      </p>
                    </div>
                    <div className="w-full sm:w-auto">
                      <WhiteCta label="Get Started" busy={false} onClick={() => handleChoose(freePlan)} />
                    </div>
                  </HoverBorderGradient>
                </motion.div>
              )}

              {/* ── Contracts / Unlimited (lit up) / Evidence ─────────── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                {/* Contracts */}
                {selectedTier && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="flex flex-col rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-7"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bricolage text-xl font-bold text-white">Contracts</h3>
                      <div className="flex rounded-full border border-white/15 bg-white/[0.04] p-0.5">
                        {contractTiers.map((tier, i) => (
                          <button
                            key={tier.code}
                            type="button"
                            onClick={() => setTierIndex(i)}
                            className={`rounded-full w-7 h-7 text-[11px] font-bold transition-colors cursor-pointer ${i === tierIndex ? 'bg-white text-black' : 'text-white/50 hover:text-white'
                              }`}
                          >
                            {tier.contract_credits}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-[13px] text-white/50 mb-6">Pay-as-you-grow for</p>

                    <div className="flex items-baseline gap-1.5 mb-1">
                      <span className="text-4xl font-black text-white tracking-tight tabular-nums">
                        ₹{animatedTierRupees.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <p className="text-[12px] text-white/40 mb-8">
                      {rupees(selectedTier.price_paise / selectedTier.contract_credits)} per contract
                    </p>

                    <FeatureList features={selectedTier.features ?? []} />
                    <WhiteCta
                      label={`Get ${selectedTier.contract_credits} contract${selectedTier.contract_credits > 1 ? 's' : ''}`}
                      busy={busyPlan === selectedTier.code}
                      onClick={() => handleChoose(selectedTier)}
                    />
                  </motion.div>
                )}

                {/* Unlimited — the highlighted slot */}
                {unlimitedPlan && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="relative flex flex-col rounded-3xl border border-[#3cb44f]/40 p-6 md:p-7 overflow-visible"
                  >
                    {/* Straddles the card's own top border, not part of the
                      content flow inside it. */}
                    <span className="absolute -top-3 -right-6 -translate-x-1/2 rounded-full bg-[#3cb44f] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1 whitespace-nowrap">
                      Popular
                    </span>

                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden"
                      style={{
                        // Tightly analogous, not complementary — bright green
                        // into emerald, both within a narrow band of the color
                        // wheel, rather than jumping to a distant/opposite hue.
                        background:
                          'radial-gradient(120% 90% at 50% 0%, rgba(92,208,111,0.42), rgba(16,185,129,0.24) 45%, transparent 70%)',
                      }}
                    />
                    <div className="relative flex flex-col h-full">
                      <h3 className="font-bricolage text-xl font-bold text-white mb-1">{unlimitedPlan.name}</h3>
                      <p className="text-[13px] text-white/60 mb-6">{unlimitedPlan.description}</p>

                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-4xl font-black text-white tracking-tight">{rupees(unlimitedPlan.price_paise)}</span>
                      </div>
                      <p className="text-[12px] text-white/40 mb-8">per 30 days</p>

                      <FeatureList features={unlimitedPlan.features ?? []} />
                      <WhiteCta
                        label={isAuthed ? `Get ${unlimitedPlan.name}` : 'Sign up & buy'}
                        busy={busyPlan === unlimitedPlan.code}
                        onClick={() => handleChoose(unlimitedPlan)}
                      />
                    </div>
                  </motion.div>
                )}

                {/* Evidence bundle */}
                {evidencePlan && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                    className="flex flex-col rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-7"
                  >
                    <h3 className="font-bricolage text-xl font-bold text-white mb-1">{evidencePlan.name}</h3>
                    <p className="text-[13px] text-white/50 mb-6">{evidencePlan.description}</p>

                    <div className="flex items-baseline gap-1.5 mb-1">
                      <span className="text-4xl font-black text-white tracking-tight">{rupees(evidencePlan.price_paise)}</span>
                    </div>
                    <p className="text-[12px] text-white/40 mb-8">per project</p>

                    <FeatureList features={evidencePlan.features ?? []} />
                    <WhiteCta
                      label={isAuthed ? 'Get Evidence Bundle' : 'Sign up & buy'}
                      busy={busyPlan === evidencePlan.code}
                      onClick={() => handleChoose(evidencePlan)}
                    />
                  </motion.div>
                )}
              </div>

              <div className="flex flex-col items-center gap-4 mt-10">
                <p className="flex items-center gap-2 text-[12px] text-white/35">
                  <ShieldCheck size={13} className="text-[#3cb44f]" />
                  Secure checkout · No commission on your work, ever
                </p>
              </div>
            </>
          )}
        </div>

        <GlassToast message={toast?.message || null} type={toast?.type} title={toast?.title} onClose={() => setToast(null)} />
      </section>
    </>
  );
}
